import { format, isToday, isYesterday } from 'date-fns'

import { cn, type AppTimezonePreference } from '@sparkai/utils'

import { NotificationComponents, NotificationTypeProps } from '../Notification'

export type NotificationGroupItem = {
  date: Date
  dateLabel?: string
  notifications: NotificationTypeProps[]
}

export type NotificationGroupProps = Readonly<{
  list: NotificationGroupItem[]
  size: 'small' | 'large'
  timezonePreference?: AppTimezonePreference
  onRowClick?: (id: string) => void
  onMarkAsRead?: (id: string) => void
}>

function getDateLabel(group: NotificationGroupItem) {
  if (group.dateLabel) return group.dateLabel

  if (isToday(group.date)) return 'Today'
  if (isYesterday(group.date)) return 'Yesterday'
  return format(group.date, 'MMMM do, yyyy')
}

export function NotificationGroup({
  list,
  size,
  timezonePreference,
  onRowClick,
  onMarkAsRead,
}: NotificationGroupProps) {
  return (
    <div
      className={cn('flex flex-col', {
        'lg:gap-4': size === 'large',
        'max-w-[420px]': size === 'small',
      })}
    >
      {list.map((group) => (
        <div
          key={group.date.getTime()}
          className={cn('flex flex-col', {
            'lg:gap-4': size === 'large',
          })}
        >
          <p className='px-3 pt-4 pb-2 text-sm font-medium text-gray-900'>{getDateLabel(group)}</p>
          <div
            className={cn('flex flex-col', {
              'lg:gap-4': size === 'large',
            })}
          >
            {group.notifications.map((notification, index) => {
              const NotificationComponent = NotificationComponents[notification.type]

              return (
                <div
                  key={notification.id}
                  className={cn({
                    'border-b border-b-gray-200 lg:border-b-0': index !== group.notifications.length - 1,
                  })}
                >
                  <NotificationComponent
                    {...notification}
                    size={size}
                    timezonePreference={timezonePreference}
                    onRowClick={onRowClick}
                    onMarkAsRead={onMarkAsRead}
                  />
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
